import { useState } from "react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, loginWithGoogle } from "../lib/auth";
import {
  submitCheckin, listMyCheckins,
  listWallPosts, createWallPost, addComment, reactPost, listComments,
} from "../lib/api";
import { Loader2, Send, Smile } from "lucide-react";

const EMOJIS = ["👏", "🔥", "❤️", "🙌", "💪"];

function CheckIn() {
  const [f, setF] = useState({ feeling: 4, week_rating: 4, need_to_talk: false, goals_this_week: "", wins_this_week: "", prayer_request: "", grade_average: "", attendance: "", behavior: "", needs_help: [], notes: "" });
  const [loading, setLoading] = useState(false);
  const qc = useQueryClient();
  const { data: history = [] } = useQuery({ queryKey: ["my-checkins"], queryFn: listMyCheckins });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleHelp = (v) => setF((s) => ({ ...s, needs_help: s.needs_help.includes(v) ? s.needs_help.filter((x) => x !== v) : [...s.needs_help, v] }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitCheckin({ ...f, feeling: Number(f.feeling), week_rating: Number(f.week_rating) });
      toast.success("Check-in saved. I got you.");
      qc.invalidateQueries({ queryKey: ["my-checkins"] });
      setF({ ...f, goals_this_week: "", wins_this_week: "", prayer_request: "", notes: "", need_to_talk: false });
    } catch { toast.error("Couldn't save check-in."); } finally { setLoading(false); }
  };

  const needsHelp = ["Food", "Clothing", "School Supplies", "Transportation", "Counseling", "Job Help", "Housing", "Medical"];
  const input = "w-full rounded-md border border-black/15 bg-white px-4 py-3 text-sm outline-none focus:border-[#6B21A8]";

  return (
    <div className="grid gap-10 md:grid-cols-12" data-testid="checkin-tab">
      <form onSubmit={submit} className="md:col-span-8 space-y-5 bg-white border border-black/10 p-6 md:p-8">
        <div>
          <p className="overline text-[#6B21A8] mb-3">How are you feeling today? (1 rough → 5 great)</p>
          <div className="flex gap-2">
            {[1,2,3,4,5].map((n) => (
              <button key={n} type="button" onClick={() => setF({ ...f, feeling: n })} data-testid={`feeling-${n}`}
                className={`h-12 w-12 rounded-md border font-display text-lg ${f.feeling === n ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15"}`}>{n}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="overline text-[#6B21A8] mb-3">Rate your week</p>
          <div className="flex gap-2">
            {[1,2,3,4,5].map((n) => (
              <button key={n} type="button" onClick={() => setF({ ...f, week_rating: n })} data-testid={`week-${n}`}
                className={`h-12 w-12 rounded-md border font-display text-lg ${f.week_rating === n ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15"}`}>{n}</button>
            ))}
          </div>
        </div>
        <label className="block"><span className="block text-sm font-medium mb-2">Wins this week</span><textarea rows="2" className={input} value={f.wins_this_week} onChange={set("wins_this_week")} data-testid="ci-wins" /></label>
        <label className="block"><span className="block text-sm font-medium mb-2">Goals this week</span><textarea rows="2" className={input} value={f.goals_this_week} onChange={set("goals_this_week")} data-testid="ci-goals" /></label>
        <label className="block"><span className="block text-sm font-medium mb-2">Prayer request (optional)</span><textarea rows="2" className={input} value={f.prayer_request} onChange={set("prayer_request")} data-testid="ci-prayer" /></label>
        <div className="grid gap-4 sm:grid-cols-3">
          <label className="block"><span className="block text-sm font-medium mb-2">Grade avg</span><input className={input} value={f.grade_average} onChange={set("grade_average")} placeholder="e.g. 3.4 / B+" /></label>
          <label className="block"><span className="block text-sm font-medium mb-2">Attendance</span><input className={input} value={f.attendance} onChange={set("attendance")} placeholder="e.g. 92%" /></label>
          <label className="block"><span className="block text-sm font-medium mb-2">Behavior</span><input className={input} value={f.behavior} onChange={set("behavior")} placeholder="e.g. no issues" /></label>
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Need help with anything?</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {needsHelp.map((h) => (
              <label key={h} className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm ${f.needs_help.includes(h) ? "border-[#6B21A8] bg-[#6B21A8]/5" : "border-black/15"}`}>
                <input type="checkbox" checked={f.needs_help.includes(h)} onChange={() => toggleHelp(h)} className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" />
                {h}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={f.need_to_talk} onChange={set("need_to_talk")} className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" data-testid="ci-talk" />
          <span className="text-sm">I'd like someone from the team to reach out this week.</span>
        </label>
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="ci-submit">
          {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>) : "Submit check-in"}
        </button>
      </form>
      <aside className="md:col-span-4">
        <p className="overline text-[#6B21A8] mb-3">Your check-ins</p>
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {history.length === 0 && <p className="text-sm text-black/60">No check-ins yet. Your first one lives here.</p>}
          {history.map((h) => (
            <div key={h.id} className="border border-black/10 p-4 text-sm">
              <p className="font-semibold">Feeling {h.feeling}/5 · Week {h.week_rating}/5</p>
              <p className="text-xs text-black/50 mt-1">{new Date(h.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</p>
              {h.wins_this_week && <p className="mt-2 text-black/70">🏆 {h.wins_this_week}</p>}
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

function Wall() {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const qc = useQueryClient();
  const { data: posts = [] } = useQuery({ queryKey: ["wall"], queryFn: listWallPosts });

  const publish = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      await createWallPost({ body: text.trim() });
      setText("");
      qc.invalidateQueries({ queryKey: ["wall"] });
    } catch { toast.error("Couldn't post."); } finally { setPosting(false); }
  };
  return (
    <div className="max-w-2xl mx-auto" data-testid="wall-tab">
      <div className="bg-white border border-black/10 p-5 mb-6">
        <textarea rows="3" className="w-full resize-none outline-none text-sm" placeholder="Share a win, a request, an encouragement…" value={text} onChange={(e) => setText(e.target.value)} data-testid="wall-input" />
        <div className="flex items-center justify-end pt-3 border-t border-black/5">
          <button onClick={publish} disabled={!text.trim() || posting} className="btn-primary" data-testid="wall-post">
            {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-2 h-4 w-4" /> Post</>}
          </button>
        </div>
      </div>
      <div className="space-y-5">
        {posts.length === 0 && <p className="text-sm text-black/60 text-center py-10">Be the first to post something.</p>}
        {posts.map((p) => <PostCard key={p.id} post={p} />)}
      </div>
    </div>
  );
}

function PostCard({ post }) {
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState("");
  const qc = useQueryClient();
  const { data: comments = [] } = useQuery({
    queryKey: ["comments", post.id], queryFn: () => listComments(post.id), enabled: showComments,
  });

  const react = async (emoji) => {
    try { await reactPost(post.id, emoji); qc.invalidateQueries({ queryKey: ["wall"] }); } catch {}
  };
  const send = async () => {
    if (!comment.trim()) return;
    try { await addComment(post.id, comment.trim()); setComment(""); qc.invalidateQueries({ queryKey: ["comments", post.id] }); qc.invalidateQueries({ queryKey: ["wall"] }); } catch {}
  };

  return (
    <article className="bg-white border border-black/10 p-5" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-3">
        {post.author_picture ? <img src={post.author_picture} alt="" className="h-10 w-10 rounded-full object-cover" /> : <div className="h-10 w-10 rounded-full bg-[#6B21A8] text-white flex items-center justify-center font-display font-bold">{post.author_name?.[0]}</div>}
        <div>
          <p className="font-semibold text-sm">{post.author_name}</p>
          <p className="text-xs text-black/50">{new Date(post.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
        </div>
      </div>
      <p className="mt-4 text-sm whitespace-pre-wrap leading-relaxed">{post.body}</p>
      <div className="mt-4 flex items-center gap-1 flex-wrap">
        {EMOJIS.map((e) => (
          <button key={e} onClick={() => react(e)} className="rounded-full border border-black/10 px-2.5 py-1 text-sm hover:bg-black/5" data-testid={`react-${e}`}>
            {e} <span className="text-xs text-black/50">{post.reactions?.[e] || 0}</span>
          </button>
        ))}
        <button onClick={() => setShowComments(!showComments)} className="ml-auto text-xs text-black/60 hover:text-black" data-testid={`toggle-comments-${post.id}`}>
          {post.comment_count || 0} comment{(post.comment_count || 0) === 1 ? "" : "s"}
        </button>
      </div>
      {showComments && (
        <div className="mt-4 pt-4 border-t border-black/5 space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="text-sm">
              <span className="font-semibold">{c.author_name}: </span>
              <span className="text-black/80">{c.body}</span>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reply…" className="flex-1 rounded-md border border-black/15 px-3 py-2 text-sm outline-none focus:border-[#6B21A8]" />
            <button onClick={send} className="btn-primary text-sm !py-2 !px-4" data-testid={`send-comment-${post.id}`}>Reply</button>
          </div>
        </div>
      )}
    </article>
  );
}

function Dashboard({ user }) {
  return (
    <div className="grid gap-6 md:grid-cols-3">
      {[
        { label: "Welcome back", value: user.name.split(" ")[0], sub: user.email },
        { label: "Role", value: user.role === "admin" ? "Admin" : "Member", sub: "Access level" },
        { label: "Next event", value: "Sept 15", sub: "Young Men's Leadership Summit" },
      ].map((c) => (
        <div key={c.label} className="bg-white border border-black/10 p-6">
          <p className="overline text-[#6B21A8]">{c.label}</p>
          <p className="mt-3 font-display text-3xl font-bold">{c.value}</p>
          <p className="text-sm text-black/60 mt-2">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

export default function Portal() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (loading) return <div className="container-x py-32 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!user) {
    return (
      <section className="container-x py-24 md:py-32 max-w-xl text-center">
        <Smile className="mx-auto h-14 w-14 text-[#6B21A8]" />
        <h1 className="mt-6 font-display text-4xl font-bold">Sign in to access the portal.</h1>
        <p className="mt-4 text-black/70">Members, parents, and mentors — one door, one brotherhood.</p>
        <button onClick={loginWithGoogle} className="btn-primary mt-8" data-testid="portal-sign-in">Sign in with Google</button>
      </section>
    );
  }

  const tabs = [
    { key: "dashboard", label: "Dashboard" },
    { key: "checkin", label: "Weekly Check-In" },
    { key: "wall", label: "Community Wall" },
  ];

  return (
    <>
      <section className="bg-[#FDFBF7] border-b border-black/10">
        <div className="container-x py-14">
          <span className="overline text-[#6B21A8]">Member Portal</span>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-bold">Welcome, {user.name.split(" ")[0]}.</h1>
        </div>
      </section>
      <div className="sticky top-16 md:top-[73px] z-20 border-b border-black/10 bg-white">
        <div className="container-x flex gap-2 py-3 overflow-x-auto" data-testid="portal-tabs">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium whitespace-nowrap ${tab === t.key ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15 text-black/70"}`}>{t.label}</button>
          ))}
        </div>
      </div>
      <section className="bg-[#FDFBF7]">
        <div className="container-x py-10 md:py-14">
          {tab === "dashboard" && <Dashboard user={user} />}
          {tab === "checkin" && <CheckIn />}
          {tab === "wall" && <Wall />}
        </div>
      </section>
    </>
  );
}
